/**
 * HoroscopePage — the real /horoscope screen.
 *
 * Fetches the single server-driven feed and renders its `items` in order via
 * WidgetRenderer. Handles loading / error / empty. The whole screen opts into
 * the design system with `askk-theme` (dark surface) on the root.
 */
import { useEffect } from 'react'
import { useApi } from '../hooks/useApi'
import { getHoroscopeFeed } from '../api/horoscope'
import { HoroscopeFeed } from './HoroscopeFeed'
import { PageState } from './PageState'
import { AstroFormPage } from './astroForm/AstroFormPage'
import { flushEvents, logEvent } from '../lib/analytics'
import { EventAction, EventSection } from '../lib/eventConstants'
import { goBack } from '../lib/bridge'
import { firePageClose, installCloseBridge, resetPageLifecycle } from './lifecycle'
import { BackArrowIcon } from './icons'

/**
 * Shared full-screen animated loader (480×853 WebP). Used for every loading
 * state in this flow: the initial feed/config fetch and the post-submit
 * re-fetch. BE-swappable (CDN URL, not bundled).
 */
const LOADER_URL = 'https://cdn-sc-g.sharechat.com/33d5318_1c8/tools/11ebfbbd_1781184150656_sc.webp'

/** Full-screen centered loader while the horoscope feed/config loads. */
function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <img
        src={LOADER_URL}
        alt="Loading your horoscope"
        decoding="async"
        className="h-auto w-full max-w-[320px] object-contain"
      />
    </div>
  )
}

export function HoroscopePage() {
  const { data, loading, error, reload } = useApi(getHoroscopeFeed)

  // Page open (on mount) + page close. Close fires when the WebView is
  // backgrounded/torn down — `pagehide` and visibility→hidden are the only
  // events that fire reliably in a mobile WebView (unlike `beforeunload`).
  // firePageClose() is idempotent, so this path and the CTA-redirect path
  // (which fires close explicitly before navigating) never double-count.
  useEffect(() => {
    resetPageLifecycle()
    logEvent({ section: EventSection.HOROSCOPE_PAGE, action: EventAction.OPEN })

    // Native back-press hook (reliable close — native awaits this before teardown).
    const uninstallCloseBridge = installCloseBridge()

    // Browser fallbacks (best-effort keepalive) for exits native doesn't intercept.
    const onPageHide = () => void firePageClose()
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') void firePageClose()
    }
    window.addEventListener('pagehide', onPageHide)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('pagehide', onPageHide)
      document.removeEventListener('visibilitychange', onVisibility)
      uninstallCloseBridge()
    }
  }, [])

  // In-page back button (Android removed its native one). We trigger the exit,
  // so we can flush the page-close event before handing off to the bridge.
  const handleBack = async () => {
    await flushEvents([firePageClose()])
    goBack()
  }

  // Server-driven gate: when the user hasn't submitted their birth details, the
  // feed comes back with this config (and items:null) and we show the form.
  const astroForm = data?.config?.astroFormData

  // Guard against a degenerate response: an empty 200 body makes `data` null, and
  // a body with items:null/non-array would crash `.length`. Anything that isn't a
  // non-empty array routes to the empty state.
  const items = data && Array.isArray(data.items) ? data.items : []

  return (
    // pb-[env(...)] keeps content clear of the Android system nav bar when the
    // WebView draws edge-to-edge; resolves to 0 (no-op) when there's no overlap.
    <div className="askk-theme min-h-screen bg-surfaceZero pb-[env(safe-area-inset-bottom)]">
      <div className="relative mx-auto max-w-[600px]">
        <button
          type="button"
          onClick={handleBack}
          aria-label="Go back"
          className="absolute left-2 top-2 z-30 flex h-10 w-10 items-center justify-center text-white"
        >
          <BackArrowIcon className="h-6 w-6" />
        </button>
        {loading && <PageLoader />}
        {error && (
          <div className="px-4">
            <PageState>
              <p>Couldn’t load your horoscope.</p>
              <button
                type="button"
                onClick={reload}
                className="mt-4 rounded-full bg-accentPrimary px-6 py-2 text-labelLarge text-textInversePrimary"
              >
                Retry
              </button>
            </PageState>
          </div>
        )}
        {!loading &&
          !error &&
          (astroForm?.shouldShowAstroForm ? (
            <AstroFormPage meta={astroForm.meta} onSubmitted={reload} />
          ) : (
            <HoroscopeFeed items={items} />
          ))}
      </div>
    </div>
  )
}

export default HoroscopePage


// this comment is to test deployment, a test commit.
